import { getApp, getAppBanner, getSectionsWithApps } from "../apps/catalog.js";
import { listFavorites } from "../apps/favorites.js";
import { navigate } from "./router.js";
import {
  fetchNextActivity,
  formatActivityDateTime,
  formatActivityWhen,
  getActivityDescription,
  getCategoryLabel,
  normalizeCategory,
} from "./next-activity.js";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isImageIcon(icon) {
  return typeof icon === "string" && (icon.startsWith("/") || icon.startsWith("http"));
}

function appIconHtml(app) {
  if (isImageIcon(app.icon)) {
    return `<img class="app-icon-img" src="${app.icon}" alt="" loading="lazy" />`;
  }
  return `<span class="app-icon-emoji" aria-hidden="true">${app.icon || "📦"}</span>`;
}

function appBannerHtml(banner) {
  if (!banner) return "";
  return `<span class="app-banner is-${banner.color}" aria-hidden="true">${escapeHtml(banner.label)}</span>`;
}

function appTileHtml(app) {
  const banner = getAppBanner(app);
  const label = banner ? `${app.name}، ${banner.label}` : app.name;
  return `
    <button type="button" class="app-tile" data-app-id="${app.id}" aria-label="${escapeHtml(label)}">
      <span class="app-icon">${appBannerHtml(banner)}${appIconHtml(app)}</span>
      <span class="app-name">${escapeHtml(app.name)}</span>
    </button>
  `;
}

function nextActivityCardHtml(activity) {
  const category = normalizeCategory(activity.category);
  const when = formatActivityWhen(activity);
  const dateLabel = formatActivityDateTime(activity);
  const participants = Array.isArray(activity.participants)
    ? activity.participants.length
    : 0;
  const creator = activity.createdByName || "";
  const footerParts = [];
  if (creator) footerParts.push(`بواسطة ${escapeHtml(creator)}`);
  if (participants) footerParts.push(`${participants} مشارك`);

  return `
    <button type="button" class="next-activity-card ${escapeHtml(category)}" data-action="open-activities">
      <div class="next-activity-top">
        <span class="next-activity-name">${escapeHtml(getActivityDescription(activity))}</span>
        <span class="next-activity-badge">${escapeHtml(getCategoryLabel(category))}</span>
      </div>
      ${when ? `<div class="next-activity-when">${escapeHtml(when)}</div>` : ""}
      ${dateLabel ? `<div class="next-activity-meta">${escapeHtml(dateLabel)}</div>` : ""}
      ${footerParts.length ? `<div class="next-activity-meta">${footerParts.join(" · ")}</div>` : ""}
    </button>
  `;
}

function renderNextActivitySection(container, activity) {
  if (!container) return;

  if (!activity) {
    container.hidden = true;
    container.innerHTML = "";
    return;
  }

  container.hidden = false;
  container.innerHTML = `
    <h2 class="section-title">النشاط القادم</h2>
    ${nextActivityCardHtml(activity)}
  `;

  const card = container.querySelector("[data-action=\"open-activities\"]");
  if (card) {
    card.addEventListener("click", () => navigate("/app/activities"));
  }
}

function bindAppTiles(root) {
  root.querySelectorAll("[data-app-id]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const search = window.location.search || "";
      navigate(`/app/${btn.getAttribute("data-app-id")}${search}`);
    });
  });
}

function renderFavoritesSection(container, favoriteIds) {
  if (!container) return;

  const apps = (favoriteIds || [])
    .map((id) => getApp(id))
    .filter(Boolean);

  if (!apps.length) {
    container.hidden = true;
    container.innerHTML = "";
    return;
  }

  container.hidden = false;
  container.innerHTML = `
    <h2 class="section-title">المفضلة</h2>
    <div class="app-grid">
      ${apps.map(appTileHtml).join("")}
    </div>
  `;

  bindAppTiles(container);
}

export function renderHome(root) {
  const sections = getSectionsWithApps();

  root.innerHTML = `
    <div class="home-shell" dir="rtl">
      <header class="home-hero">
        <div class="home-brand" dir="ltr">
          <img class="home-brand-mark" src="/img/welcome2life-icon.png" alt="" />
          <div class="home-brand-text">
            <h1 class="home-brand-name">Welcome 2 Life</h1>
            <p class="home-tagline">عالم تنتمي إليه</p>
          </div>
        </div>
      </header>

      <main class="home-sections" id="home-sections">
        <section class="app-section next-activity-section" id="next-activity-section" hidden></section>
        <section class="app-section favorites-section" id="favorites-section" hidden></section>
        ${sections
          .map(
            (section) => `
          <section class="app-section" data-section="${section.id}">
            <h2 class="section-title">${section.titleAr || section.title}</h2>
            <div class="app-grid">
              ${section.apps.map(appTileHtml).join("")}
            </div>
          </section>
        `
          )
          .join("")}
      </main>
    </div>
  `;

  bindAppTiles(root);

  const nextSection = root.querySelector("#next-activity-section");
  fetchNextActivity()
    .then((activity) => {
      renderNextActivitySection(nextSection, activity);
    })
    .catch(() => {
      renderNextActivitySection(nextSection, null);
    });

  const favoritesSection = root.querySelector("#favorites-section");
  listFavorites()
    .then((payload) => {
      renderFavoritesSection(favoritesSection, payload.myFavoriteIds);
    })
    .catch(() => {
      renderFavoritesSection(favoritesSection, []);
    });
}
